import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check } from "lucide-react";

/**
 * UK flag (Union Jack) SVG.
 */
const UKFlag = ({ size = 18 }) => (
  <svg
    viewBox="0 0 60 30"
    width={size}
    height={(size * 30) / 60}
    aria-hidden="true"
    style={{ display: "block", borderRadius: 2, flexShrink: 0 }}
  >
    <clipPath id="uks"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
    <clipPath id="ukt">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
    </clipPath>
    <g clipPath="url(#uks)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#ukt)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

/**
 * Vietnam flag SVG.
 */
const VNFlag = ({ size = 18 }) => (
  <svg
    viewBox="0 0 30 20"
    width={size}
    height={(size * 20) / 30}
    aria-hidden="true"
    style={{ display: "block", borderRadius: 2, flexShrink: 0 }}
  >
    <rect width="30" height="20" fill="#DA251D" />
    <polygon
      points="15,4 16.76,9.42 22.46,9.42 17.85,12.76 19.61,18.18 15,14.84 10.39,18.18 12.15,12.76 7.54,9.42 13.24,9.42"
      fill="#FFFF00"
    />
  </svg>
);

const OPTIONS = [
  { code: "en", label: "EN", Flag: UKFlag },
  { code: "vi", label: "VI", Flag: VNFlag },
];

/**
 * LanguageSwitcher — dropdown showing current flag + code, opens on click.
 * variant="dark" → light text on dark navy nav
 * variant="light" → dark text on light background (e.g. mobile menu bg)
 */
export default function LanguageSwitcher({ variant = "dark", align = "right" }) {
  const { i18n } = useTranslation();
  const currentCode = i18n.language && i18n.language.startsWith("vi") ? "vi" : "en";
  const current = OPTIONS.find((o) => o.code === currentCode) || OPTIONS[0];
  const CurrentFlag = current.Flag;

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const change = (code) => {
    if (code !== currentCode) i18n.changeLanguage(code);
    setOpen(false);
  };

  const isDark = variant === "dark";
  const btnCls = isDark
    ? "text-white border-white/30 hover:border-unik-gold hover:text-unik-gold"
    : "text-[#14355a] border-[#14355a]/25 hover:border-[#14355a] hover:text-[#14355a]";
  const panelBg = isDark
    ? "bg-[#14355a] border-white/15"
    : "bg-white border-[#14355a]/15";
  const itemText = isDark ? "text-white/85 hover:text-unik-gold hover:bg-white/[0.06]" : "text-[#14355a] hover:bg-[#14355a]/[0.06]";
  const activeText = isDark ? "text-unik-gold" : "text-unik-gold-soft";

  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language / Ngôn ngữ"
        data-testid="lang-toggle"
        className={`inline-flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-full border text-[12px] font-medium tracking-wide transition-all duration-300 ${btnCls}`}
      >
        <CurrentFlag size={16} />
        <span data-testid="lang-current">{current.label}</span>
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="lang-menu"
          className={`absolute top-full mt-2 min-w-[128px] rounded-md border shadow-xl overflow-hidden ${panelBg} ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          {OPTIONS.map((o) => {
            const Flag = o.Flag;
            const active = o.code === currentCode;
            return (
              <button
                type="button"
                key={o.code}
                role="option"
                aria-selected={active}
                data-testid={`lang-option-${o.code}`}
                onClick={() => change(o.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium tracking-wide transition-colors ${itemText} ${
                  active ? activeText : ""
                }`}
              >
                <Flag size={16} />
                <span className="flex-1 text-left">{o.label}</span>
                {active && <Check size={13} className="opacity-90" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
